<template>
  <v-container>
    <!-- Summary chips -->
    <v-row class="mb-3" dense>
      <v-col cols="12" sm="4">
        <v-card outlined>
          <v-card-text class="py-3">
            <div class="text-subtitle-2 grey--text">
              Total Entries
            </div>
            <div class="text-h5 font-weight-bold">
              {{ serverItemsLength >= 0 ? serverItemsLength : '—' }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card outlined>
          <v-card-text class="py-3">
            <div class="text-subtitle-2 success--text">
              Qty In (page)
            </div>
            <div class="text-h5 font-weight-bold success--text">
              {{ totalIn.toLocaleString(undefined, { minimumFractionDigits: 2 }) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card outlined>
          <v-card-text class="py-3">
            <div class="text-subtitle-2 error--text">
              Qty Out (page)
            </div>
            <div class="text-h5 font-weight-bold error--text">
              {{ totalOut.toLocaleString(undefined, { minimumFractionDigits: 2 }) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters Row 1: search/filter fields -->
    <v-row class="mb-2" dense>
      <v-col cols="12" sm="6" md="3">
        <v-select
          v-model="filterStoreId"
          :items="[{ id: null, name: 'All Stores' }, ...branchList]"
          item-text="name"
          item-value="id"
          label="Store"
          dense
          outlined
          hide-details
          clearable
        />
      </v-col>

      <v-col cols="12" sm="6" md="5">
        <v-autocomplete
          v-model="filterStockId"
          :loading="stockLoading"
          :items="stockList"
          :search-input.sync="stockSearch"
          no-filter
          item-value="id"
          :item-text="stockItemText"
          label="Stock"
          dense
          outlined
          hide-details
          clearable
        >
          <template #item="{ item }">
            <span>{{ item.product_name }} <span class="grey--text">({{ item.stock_code }})</span></span>
          </template>
          <template #selection="{ item }">
            <span>{{ item.product_name }}</span>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="12" sm="6" md="2">
        <v-select
          v-model="filterType"
          :items="typeItems"
          label="Type"
          dense
          outlined
          hide-details
          clearable
        />
      </v-col>

      <v-col cols="12" sm="6" md="2">
        <v-select
          v-model="filterDirection"
          :items="[{ text: 'In', value: 'in' }, { text: 'Out', value: 'out' }]"
          label="Direction"
          dense
          outlined
          hide-details
          clearable
        />
      </v-col>
    </v-row>

    <!-- Filters Row 2: date range + download -->
    <v-row class="mb-3" dense align="center">
      <v-col cols="12" sm="4" md="3">
        <v-text-field
          v-model="dateStart"
          label="From"
          type="date"
          dense
          outlined
          hide-details
          clearable
        />
      </v-col>

      <v-col cols="12" sm="4" md="3">
        <v-text-field
          v-model="dateEnd"
          label="To"
          type="date"
          dense
          outlined
          hide-details
          clearable
        />
      </v-col>

      <v-col cols="12" sm="4" md="2">
        <v-btn
          color="green"
          dark
          block
          height="40"
          :loading="excelLoading"
          @click="downloadExcel"
        >
          <v-icon left>mdi-download</v-icon>
          Download Excel
        </v-btn>
      </v-col>
    </v-row>

    <!-- Table -->
    <v-data-table
      :server-items-length="serverItemsLength"
      :items-per-page.sync="itemsPerPage"
      :page.sync="page"
      dense
      class="elevation-1"
      :headers="headers"
      :items="rows"
      :loading="loading"
      :footer-props="{ 'items-per-page-options': [10, 20, 50, 100] }"
    >
      <template #item.type="{ value }">
        <v-chip x-small label dark :color="typeConf(value).color">
          <v-icon x-small left>
            {{ typeConf(value).icon }}
          </v-icon>
          {{ typeConf(value).label }}
        </v-chip>
      </template>

      <template #item.direction="{ value }">
        <v-chip x-small label :color="value === 'in' ? 'success' : 'error'" dark>
          <v-icon x-small left>
            {{ value === 'in' ? 'mdi-arrow-down' : 'mdi-arrow-up' }}
          </v-icon>
          {{ value }}
        </v-chip>
      </template>

      <template #item.quantity="{ item }">
        <span :class="item.direction === 'out' ? 'error--text' : 'success--text'">
          {{ item.direction === 'out' ? '-' : '+' }}{{ item.quantity }}
        </span>
      </template>

      <template #item.balance="{ value }">
        <span class="font-weight-medium">{{ value }}</span>
      </template>

      <template #item.sourceRef="{ item }">
        <a
          v-if="item.sourceId"
          class="text-decoration-underline"
          style="cursor: pointer"
          @click="$refs.sourceRefDialog.open(item.journalId)"
        >{{ item.sourceRef }}</a>
        <span v-else>{{ item.sourceRef }}</span>
      </template>

      <template #item.actual_date="{ value }">
        {{ value ? (value + '').dateFormat() : '—' }}
      </template>

      <template #item.created_at="{ value }">
        {{ value ? (value + '').dateFormat() : '—' }}
      </template>

      <template #no-data>
        <div class="pa-6 text-center">
          <v-icon x-large class="mb-2">
            mdi-book-open-outline
          </v-icon>
          <div class="font-weight-medium">
            No journal entries found
          </div>
          <div class="text-caption grey--text">
            Adjust filters or date range
          </div>
        </div>
      </template>
    </v-data-table>

    <source-ref-dialog ref="sourceRefDialog" />
  </v-container>
</template>

<script>
import { authAxios } from '@/extra-functions/axios.extra'
import { ErrorPopup, useDebounce } from '@/lib/utils'
import { mapState } from 'vuex'
import createWorkBook from '@/extra-functions/writeExcel.extra.js'

function resolveSourceRef(type, src, sourceId, referenceNo) {
  if (referenceNo) return referenceNo
  if (!src) return sourceId ?? '—'
  if (type === 'credit' || type === 'credit_reversal') return src.consumer_loan_id ?? sourceId ?? '—'
  return src.receipt_no ?? src.csi_number ?? sourceId ?? '—'
}

const JOURNAL_TYPE_CONF = {
  sale: { label: 'Cash Sale',         icon: 'mdi-cash-register',         color: 'blue' },
  purchase: { label: 'Purchase',          icon: 'mdi-cart-arrow-down',        color: 'orange darken-1' },
  transfer: { label: 'Transfer Out',      icon: 'mdi-transfer-right',         color: 'purple' },
  receive: { label: 'Received',          icon: 'mdi-transfer-down',          color: 'teal' },
  loss: { label: 'Stock Loss',        icon: 'mdi-alert-circle-outline',   color: 'red darken-1' },
  credit: { label: 'Credit Sale',       icon: 'mdi-credit-card-outline',    color: 'indigo' },
  adjustment: { label: 'Adjustment',        icon: 'mdi-tune',                   color: 'brown' },
  initial: { label: 'Initial Stock',     icon: 'mdi-flag-checkered',         color: 'blue-grey' },
  sale_promo: { label: 'Promo Sale',        icon: 'mdi-tag-heart',              color: 'cyan darken-1' },
  return: { label: 'Good Return',       icon: 'mdi-keyboard-return',        color: 'green' },
  return_bad_order: { label: 'Bad Order Return',  icon: 'mdi-package-variant-remove', color: 'deep-orange' },
  sale_reversal: { label: 'Sale Reversal',     icon: 'mdi-undo',                   color: 'blue-grey darken-1' },
  purchase_reversal: { label: 'Purchase Reversal', icon: 'mdi-undo',                   color: 'blue-grey darken-1' },
  transfer_reversal: { label: 'Transfer Reversal', icon: 'mdi-undo',                   color: 'blue-grey darken-1' },
  receive_reversal: { label: 'Receive Reversal',  icon: 'mdi-undo',                   color: 'blue-grey darken-1' },
  loss_reversal: { label: 'Loss Reversal',     icon: 'mdi-undo',                   color: 'blue-grey darken-1' },
  credit_reversal: { label: 'Credit Reversal',   icon: 'mdi-undo',                   color: 'blue-grey darken-1' },
  adjustment_reversal: { label: 'Adj. Reversal',     icon: 'mdi-tune',                   color: 'blue-grey darken-1' },
  initial_reversal: { label: 'Initial Reversal',  icon: 'mdi-undo',                   color: 'blue-grey darken-1' },
  sale_promo_reversal: { label: 'Promo Reversal',    icon: 'mdi-undo',                   color: 'blue-grey darken-1' },
  return_reversal: { label: 'Return Reversal',   icon: 'mdi-undo',                   color: 'blue-grey darken-1' },
  return_bad_order_reversal: { label: 'Bad Order Rev.',    icon: 'mdi-undo',                   color: 'blue-grey darken-1' },
}

const TYPES = Object.keys(JOURNAL_TYPE_CONF)

export default {
  name: 'InventoryBreakdown',

  components: {
    SourceRefDialog: () =>
      import('@/views/dashboard/components/inventory-monitoring/SourceRefDialog.vue'),
  },

  data() {
    return {
      filterStoreId: null,
      filterStockId: null,
      filterType: null,
      filterDirection: null,
      dateStart: null,
      dateEnd: null,

      stockList: [],
      stockSearch: null,
      stockLoading: false,
      stockDebounce: useDebounce(400),

      loading: false,
      excelLoading: false,
      rows: [],
      page: 1,
      itemsPerPage: 20,
      serverItemsLength: -1,

      typeItems: TYPES.map(t => ({ text: JOURNAL_TYPE_CONF[t]?.label ?? t, value: t })),

      headers: [
        { text: 'Type', value: 'type', sortable: false },
        { text: 'Dir', value: 'direction', sortable: false, width: 70 },
        { text: 'Store', value: 'storeName', sortable: false },
        { text: 'Stock', value: 'stockName', sortable: false },
        { text: 'Qty', value: 'quantity', sortable: false, align: 'right' },
        { text: 'Qty Remaining', value: 'balance', sortable: false, align: 'right' },
        { text: 'Unit', value: 'unit', sortable: false },
        { text: 'Source Ref', value: 'sourceRef', sortable: false },
        { text: 'Consumer', value: 'consumerName', sortable: false },
        { text: 'Actual Date', value: 'actual_date', sortable: false },
        { text: 'Timestamp', value: 'created_at', sortable: false },
      ],
    }
  },

  computed: {
    ...mapState({
      branchList: (state) => state.branchLocation.list,
    }),

    storeMap() {
      const m = {}
      for (const s of this.branchList) m[s.id] = s.name
      return m
    },

    stockMap() {
      const m = {}
      for (const s of this.stockList) m[s.id] = `${s.product_name} (${s.stock_code})`
      return m
    },

    totalIn() {
      return this.rows.filter(r => r.direction === 'in').reduce((s, r) => s + Number(r.quantity || 0), 0)
    },

    totalOut() {
      return this.rows.filter(r => r.direction === 'out').reduce((s, r) => s + Number(r.quantity || 0), 0)
    },
  },

  watch: {
    page() { this.fetchRows() },
    itemsPerPage() { this.page = 1; this.fetchRows() },
    filterStoreId() { this.page = 1; this.fetchRows() },
    filterStockId() { this.page = 1; this.fetchRows() },
    filterType() { this.page = 1; this.fetchRows() },
    filterDirection() { this.page = 1; this.fetchRows() },
    dateStart() { this.page = 1; this.fetchRows() },
    dateEnd() { this.page = 1; this.fetchRows() },
    stockSearch(val) {
      this.stockDebounce(() => this.fetchStockList(val))
    },
  },

  created() {
    this.fetchRows()
    this.fetchStockList()
  },

  methods: {
    stockItemText(s) {
      return `${s.product_name} (${s.stock_code})`
    },

    async fetchRows() {
      this.loading = true
      try {
        const { data } = await authAxios().get('/v2/inventory-journals', {
          params: {
            per_page: this.itemsPerPage,
            page: this.page,
            store_id: this.filterStoreId || undefined,
            stock_id: this.filterStockId || undefined,
            type: this.filterType || undefined,
            direction: this.filterDirection || undefined,
            date_start: this.dateStart || undefined,
            date_end: this.dateEnd || undefined,
          },
        })
        const items = data.data ?? []
        this.serverItemsLength = data.total ?? items.length
        // journalId/sourceId power the source popup; keep them off mapRow so
        // the excel export columns stay unchanged
        this.rows = items.map(r => ({
          ...this.mapRow(r),
          journalId: r.id,
          sourceId: r.source_id,
        }))
      } catch (e) {
        console.error(e)
        ErrorPopup.fire('Failed to load journal entries.')
      } finally {
        this.loading = false
      }
    },

    mapRow(r) {
      const sourceRef = resolveSourceRef(r.type, r.source, r.source_id, r.reference_no)
      return {
        type: r.type,
        direction: r.direction,
        storeName: this.storeMap[r.store_id] ?? r.store_id,
        stockName: r.stock
          ? `${r.stock.product_name} (${r.stock.stock_code})`
          : (this.stockMap[r.stock_id] ?? r.stock_id ?? '—'),
        quantity: r.quantity,
        balance: Math.round(Number(r.balance || 0) * 100) / 100,
        unit: r.unit,
        sourceRef,
        consumerName: r.consumer_name || '—',
        actual_date: r.actual_date,
        created_at: r.created_at,
      }
    },

    async fetchStockList(search = null) {
      this.stockLoading = true
      try {
        const { data } = await authAxios().get('/v2/stocks/dropdown', {
          params: { search: search || undefined },
        })
        this.stockList = data.data ?? []
      } catch (e) {
        console.error(e)
      } finally {
        this.stockLoading = false
      }
    },

    typeConf(t) {
      return JOURNAL_TYPE_CONF[t] ?? { label: t, icon: 'mdi-help-circle-outline', color: 'grey' }
    },

    async downloadExcel() {
      this.excelLoading = true
      try {
        const { data } = await authAxios().get('/v2/inventory-journals/export', {
          params: {
            store_id: this.filterStoreId || undefined,
            stock_id: this.filterStockId || undefined,
            type: this.filterType || undefined,
            direction: this.filterDirection || undefined,
            date_start: this.dateStart || undefined,
            date_end: this.dateEnd || undefined,
          },
        })
        const mapped = data.map(r => this.mapRow(r))
        await createWorkBook('', `product_movement_${Date.now()}`, mapped)
      } catch (e) {
        console.error(e)
        ErrorPopup.fire('Failed to export.')
      } finally {
        this.excelLoading = false
      }
    },
  },
}
</script>
